import React, { useRef } from 'react';
import { debounce } from 'lodash';
import createInlineFormButton from './utils/createInlineFormButton';
import LinkIcon from '@material-ui/icons/Link';
import TextField from '@material-ui/core/TextField';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import Autocomplete /* { createFilterOptions } */ from '@material-ui/lab/Autocomplete';
import CircularProgress from '@material-ui/core/CircularProgress';
import {
  makeStyles,
  MuiThemeProvider,
  useTheme,
} from '@material-ui/core/styles';
import fuzzysort from 'fuzzysort';
import { EditorState, Modifier } from 'draft-js';
import isUrl from 'is-url-superb';
import withStores from '../../stores/withStores';
import { postSearch } from '../../api/search';
import { postMedia } from '../../api/media';
import adaTheme from '../../theme';
import useI18n from '../../i18n/useI18n';

const applyEntity = (editorState, entity, selection) => {
  const contentState = editorState.getCurrentContent();
  const contentStateWithEntity = contentState.createEntity(
    entity.type,
    entity.mutability,
    entity.data,
  );
  const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
  const selectionForEntity = selection || editorState.getSelection();
  const newContentStateWithEntity = Modifier.applyEntity(
    contentStateWithEntity,
    selectionForEntity,
    entityKey,
  );
  return EditorState.push(
    editorState,
    newContentStateWithEntity,
    'apply-entity',
  );
};

const byRelevance = (a, b) => {
  // if (a.score > b.score) return -1;
  // if (a.score < b.score) return 1;
  const dateA = a.updatedAt || a.createdAt;
  const dateB = b.updatedAt || b.createdAt;
  if (new Date(dateA) < new Date(dateB)) return 1;
  if (new Date(dateA) > new Date(dateB)) return -1;
  return 0;
};

const isMailToLink = link => {
  const startsWithMailTo = link.startsWith('mailto:');
  return startsWithMailTo;
};

export const getLabelFromTag = tag => tag.substr(tag.indexOf(':') + 1);
const getImportantTags = tags =>
  tags.filter(t => t.includes('Seite:')).map(t => getLabelFromTag(t));

// const filterOptions = createFilterOptions({
//   stringify: option => option.result.title,
// });

const State = ({ state }) => {
  const i18n = useI18n();
  return (
    <Box style={{ lineHeight: 1 }} mx={1} mb={0.5} color={`state.${state}`}>
      <Typography variant="caption">{i18n.t(`LinkButton.${state}`)}</Typography>
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(1),
  },
  optionContainer: {
    borderBottom: `${theme.palette.grey[200]} 1px solid`,
  },
  option: {
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
  },
  type: {
    textTransform: 'capitalize',
  },
  tag: {
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(0.5),
  },
  chip: {
    marginRight: theme.spacing(1),
    pointerEvents: 'none',
    marginBottom: theme.spacing(0.5),
  },
}));

const LinkForm = props => {
  const inputEl = React.useRef(null);
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState([]);
  const [query, setQuery] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const classes = useStyles(props);
  const i18n = useI18n();

  React.useEffect(() => {
    props.appStore.setPaused(true);
    inputEl.current.focus();
  }, []);

  const handleClose = () => {
    props.onOverrideContent(null);
    setOpen(false);
    props.appStore.setPaused(false);
  };

  const startSearch = useRef(
    debounce(query => {
      setIsLoading(true);
      setOpen(true);
      postSearch(query, ['pages', 'media'], 9999).then(r => {
        setIsLoading(false);
        const flatten = r.data.results.map(d => d.result);
        const fuzzyMatched = fuzzysort.go(query, flatten, {key: 'title'}).map(d => d.obj);
        const originalWithoutFuzzyMatched = flatten.filter(d => fuzzyMatched.findIndex(fm => fm.uuid === d.uuid) === -1).sort(byRelevance);
        setOptions([...fuzzyMatched, ...originalWithoutFuzzyMatched.slice(0, 30)]);
      });
    }, 500),
  ).current;

  const handleInput = e => {
    const query = e.target.value;
    if (query.length >= 3 && !isLoading) {
      startSearch(query);
    }
    setQuery(query);
  };

  const createEntity = entity => {
    const editorState = applyEntity(props.editorState, entity);
    props.setEditorState(editorState);
    handleClose();
  };

  const handleSubmit = e => {
    if (e.key === 'Enter') {
      const isQueryValid = isMailToLink(query) || isUrl(query);
      if (isQueryValid) {
        const newItem = {
          viewAccess: [],
          altText: '',
          state: 'public',
          title: '',
          sourceType: 'unknown',
          class: 'URL',
          source: query,
        };
        postMedia(newItem).then(r => {
          const entity = {
            type: 'media-link',
            mutability: 'MUTABLE',
            data: {
              uuid: r.data.uuid,
            },
          };
          createEntity(entity);
        });
      }
    }
  };

  const handleSelectOption = (e, option) => {
    e.stopPropagation();
    const type = option.type ? 'page' : 'media';
    const entity = {
      type: `${type}-link`,
      mutability: 'MUTABLE',
      data: {
        uuid: option.uuid,
      },
    };
    createEntity(entity);
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div className={classes.root}>
        <Autocomplete
          id="asynchronous-demo"
          style={{ width: 350 }}
          classes={{
            option: classes.optionContainer,
          }}
          open={open}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={handleClose}
          noOptionsText={
            <Typography variant="body1" color="textPrimary">
              {i18n.t('LinkButton.noOptionsText')}
            </Typography>
          }
          onInputChange={handleInput}
          getOptionLabel={option => option.title || option.source}
          options={options}
          loading={isLoading}
          loadingText={i18n.t('LinkButton.loadingText')}
          filterOptions={options => options}
          onKeyDown={handleSubmit}
          renderOption={( result ) => {
            return (
              <div
                className={classes.option}
                onClick={e => {
                  handleSelectOption(e, result);
                }}
              >
                <Box>
                  <Typography variant="body1">
                    {result.title || result.source}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" flexWrap="wrap">
                  <Box mb={0.5} style={{ lineHeight: 1 }}>
                    <Typography className={classes.type} variant="caption">
                      {result.type || result.class}
                    </Typography>
                  </Box>
                  <State state={result.state} />
                  {result.tags &&
                    result.tags.map(t => (
                      <Chip
                        className={classes.chip}
                        key={t}
                        label={t}
                        size="small"
                        color="secondary"
                      />
                    ))}
                </Box>
              </div>
            );
          }}
          renderInput={params => {
            return (
              <TextField
                {...params}
                label={i18n.t('LinkButton.label')}
                fullWidth
                value={query}
                variant="outlined"
                inputRef={inputEl}
                helperText={i18n.t('LinkButton.helperText')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {isLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            );
          }}
        />
      </div>
    </ClickAwayListener>
  );
};

const LinkFormWithTheme = props => (
  <MuiThemeProvider theme={adaTheme}>
    <LinkForm {...props} />
  </MuiThemeProvider>
);

export default createInlineFormButton({
  form: withStores('appStore')(LinkFormWithTheme),
  name: 'link-bottom',
  children: <LinkIcon fontSize="small" />,
});
